import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Sparkle, ArrowsClockwise, Infinity as InfinityIcon, MagicWand } from "@phosphor-icons/react";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { apiClient } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const PRESET_LIBRARY = {
  nature: {
    label: "Nature",
    presets: [
      { label: "Sea Waves", prompt: "Calm ocean waves gently rolling onto a sandy shore, soft foamy wash receding over pebbles, distant seagulls, relaxed steady rhythm, open-air seaside ambience" },
      { label: "Storm & Rain", prompt: "Steady rain falling on leaves with distant rolling thunder slowly approaching, occasional close rumble, wet outdoor night atmosphere" },
      { label: "Forest Morning", prompt: "Peaceful forest morning ambience, songbirds chirping at varying distances, light breeze rustling leaves, occasional woodpecker taps far away" },
      { label: "Campfire", prompt: "Small campfire crackling and popping, soft flame hiss, occasional wood snap, intimate close-up recording, quiet night background" },
    ],
  },
  theater: {
    label: "Theater",
    presets: [
      { label: "Applause", prompt: "Large theater audience erupting into enthusiastic applause with scattered cheers and bravos, warm hall reverb, swelling then settling" },
      { label: "Curtain", prompt: "Heavy velvet stage curtain sliding open on rails, fabric swoosh with mechanical pulley squeaks, large hall acoustics" },
      { label: "Stage Steps", prompt: "Slow deliberate footsteps on a hollow wooden stage floor, leather soles, each step resonating in a quiet empty theater" },
      { label: "Audience Murmur", prompt: "Murmuring theater audience before a performance, indistinct conversations, program pages rustling, seats creaking, large hall ambience" },
    ],
  },
  cinema: {
    label: "Cinema",
    presets: [
      { label: "Trailer Hit", prompt: "Massive cinematic braam impact hit with deep sub-bass boom, metallic resonance tail, dramatic trailer transition, huge reverb decay" },
      { label: "Heartbeat", prompt: "Deep loud cinematic human heartbeat, slow heavy lub-dub rhythm repeating steadily and continuously without pauses for the entire duration, powerful low-frequency thumps, close and intimate, tense thriller scene, seamless steady loop" },
      { label: "Tension", prompt: "Rising cinematic tension riser, low ominous drone swelling with heartbeat-like pulses, building to a sharp cutoff" },
      { label: "Whoosh Transition", prompt: "Fast airy whoosh transition sweeping from left to right, smooth doppler effect, clean tail, modern cinematic sound design" },
      { label: "Car Chase", prompt: "Car speeding past with engine roar and tire screech on asphalt, doppler pass-by, urban street echo, action chase energy" },
    ],
  },
  cartoon: {
    label: "Cartoon",
    presets: [
      { label: "Boing!", prompt: "Classic cartoon spring boing bounce with wobbling metallic twang, exaggerated comedic pitch bend, short and punchy" },
      { label: "Run Away", prompt: "Cartoon character feet spinning then zooming away with a comedic whip whoosh, bongo run steps fading fast into the distance" },
      { label: "Slip & Fall", prompt: "Cartoon slide whistle descending pitch followed by a comedic thud and dishes clattering, slapstick timing" },
      { label: "Magic Sparkle", prompt: "Sparkling magical shimmer with ascending glissando chimes, fairy dust twinkle, bright playful cartoon magic spell" },
    ],
  },
  horror: {
    label: "Horror",
    presets: [
      { label: "Creaking Door", prompt: "Old heavy wooden door creaking open very slowly, rusty iron hinges groaning, echoing in a dark abandoned mansion hallway" },
      { label: "Whispers", prompt: "Unsettling layered ghostly whispers swirling around, indistinct breathy voices, cold hollow reverb, slowly intensifying dread" },
      { label: "Attic", prompt: "Creaking floorboards above, slow dragging sound across old wood, dust settling, tense silence between movements, haunted attic" },
      { label: "Dark Drone", prompt: "Deep sinister horror drone, dissonant low strings texture, subtle metallic scrapes emerging and fading, suffocating atmosphere" },
    ],
  },
  action: {
    label: "Action",
    presets: [
      { label: "Explosion", prompt: "Huge explosion with sharp initial crack, deep sub-bass shockwave, debris scattering and raining down, long outdoor echo tail" },
      { label: "Swords", prompt: "Two steel swords clashing repeatedly in a duel, sharp metallic rings with scrapes and slides, echoing in a stone courtyard" },
      { label: "Gunshot", prompt: "Single powerful rifle gunshot with tight mechanical crack, long echo rolling across an open valley, distant bird flutter after" },
      { label: "Shattering Glass", prompt: "Large glass window shattering violently, shards cascading and tinkling onto a hard floor, sharp initial crash with sparkling decay" },
    ],
  },
  scifi: {
    label: "Sci-Fi",
    presets: [
      { label: "Starship", prompt: "Massive sci-fi spaceship engine powering up from low hum to thunderous plasma roar, electrical crackles, deep resonant vibration" },
      { label: "Laser", prompt: "Bright sci-fi laser blaster shots, zappy energy discharge with pitch drop, quick electronic decay, futuristic weapon fire" },
      { label: "Teleport", prompt: "Futuristic teleportation effect, shimmering energy build-up with rising synthetic swirl, sudden vacuum pop and airy dissolve" },
      { label: "Robot", prompt: "Robot servo motors whirring and clicking as a mechanical android walks, hydraulic hisses, precise metallic joint movements" },
    ],
  },
  ambient: {
    label: "Ambient",
    presets: [
      { label: "Night City", prompt: "Distant city night ambience, sparse traffic hum, occasional far-away siren, faint wind between buildings, calm urban atmosphere" },
      { label: "Old House", prompt: "Quiet old house room tone, subtle wooden creaks, ticking wall clock, faint wind outside the windows, cozy still atmosphere" },
      { label: "Cafe", prompt: "Cozy cafe ambience, soft murmur of conversations, espresso machine hissing, cups and saucers clinking, relaxed daytime mood" },
      { label: "Deep Space", prompt: "Deep space ambience, vast low frequency drone, subtle ethereal shimmer, slow evolving cosmic texture, weightless emptiness" },
    ],
  },
};

export default function SoundGenerator({ onGenerated, credits }) {
  const [prompt, setPrompt] = useState("");
  const [duration, setDuration] = useState([5]);
  const [autoDuration, setAutoDuration] = useState(true);
  const [influence, setInfluence] = useState([30]);
  const [loop, setLoop] = useState(false);
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [generating, setGenerating] = useState(false);
  const { t } = useLang();
  const [enhancing, setEnhancing] = useState(false);
  const [autoEnhance, setAutoEnhance] = useState(() => localStorage.getItem("aurafx_auto_enhance") !== "0");
  const [presetGroup, setPresetGroup] = useState("nature");
  const [batchMode, setBatchMode] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null);

  const toggleAutoEnhance = (v) => {
    setAutoEnhance(v);
    localStorage.setItem("aurafx_auto_enhance", v ? "1" : "0");
  };

  const enhance = async () => {
    if (prompt.trim().length < 2) {
      toast.error("Enter a sound idea, e.g. “sea waves”");
      return;
    }
    setEnhancing(true);
    try {
      const { data } = await apiClient.post("/prompts/enhance", {
        prompt: prompt.trim(),
        category: category || null,
        duration_seconds: autoDuration ? null : duration[0],
      });
      setPrompt(data.enhanced_prompt);
      toast.success("Prompt enhanced ✨");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't enhance prompt");
    } finally {
      setEnhancing(false);
    }
  };

  useEffect(() => {
    apiClient.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  const generate = async () => {
    if (batchMode) {
      const lines = prompt.split("\n").map((l) => l.trim()).filter((l) => l.length >= 3).slice(0, 5);
      if (lines.length === 0) {
        toast.error("Prompt is too short");
        return;
      }
      if (credits < lines.length) {
        toast.error(`Need ${lines.length} credits`);
        window.dispatchEvent(new CustomEvent("aurafx:out-of-credits"));
        return;
      }
      setGenerating(true);
      let ok = 0;
      for (let i = 0; i < lines.length; i++) {
        setBatchProgress(`${i + 1} / ${lines.length}`);
        try {
          const { data } = await apiClient.post("/sounds/generate", {
            prompt: lines[i],
            duration_seconds: autoDuration ? null : duration[0],
            prompt_influence: influence[0] / 100,
            loop,
            category: category || null,
            auto_enhance: autoEnhance,
          });
          onGenerated(data);
          ok++;
        } catch (e) {
          toast.error(`${i + 1}: ${e.response?.data?.detail || "failed"}`);
          if (e.response?.status === 402) break;
        }
      }
      setBatchProgress(null);
      setGenerating(false);
      if (ok > 0) toast.success(`${ok} ${t("batchDone")}`);
      return;
    }
    if (prompt.trim().length < 3) {
      toast.error("Prompt is too short");
      return;
    }
    if (credits <= 0) {
      toast.error("No credits left");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await apiClient.post("/sounds/generate", {
        prompt: prompt.trim(),
        duration_seconds: autoDuration ? null : duration[0],
        prompt_influence: influence[0] / 100,
        loop,
        category: category || null,
        auto_enhance: autoEnhance,
      });
      toast.success("Sound generated");
      onGenerated(data);
    } catch (e) {
      const detail = e.response?.data?.detail || "Generation failed";
      toast.error(detail);
      if (e.response?.status === 402) {
        // bubble up — Dashboard catches via callback
        window.dispatchEvent(new CustomEvent("aurafx:out-of-credits"));
      }
      if (e.response?.status === 502) window.dispatchEvent(new CustomEvent("aurafx:engine-check"));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <section
      data-testid="generator-panel"
      className="rounded-2xl border border-white/10 bg-[color:var(--surface)] overflow-hidden"
    >
      <div className="px-6 py-5 border-b border-white/5 flex items-center justify-between">
        <div>
          <p className="text-xs font-mono-num uppercase tracking-widest text-white/40">{t("promptKicker")}</p>
          <h2 className="font-display font-semibold text-xl tracking-tight mt-0.5">{t("generatorTitle")}</h2>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="batch-mode-toggle"
            onClick={() => setBatchMode((b) => !b)}
            className={`text-[10px] px-2.5 py-1 rounded-full font-mono-num uppercase tracking-widest border transition-colors ${
              batchMode ? "bg-[color:var(--accent)] border-[color:var(--accent)] text-white" : "border-white/15 text-white/50 hover:text-white"
            }`}
          >
            {t("batchMode")}
          </button>
          <div className="text-xs font-mono-num text-white/40 uppercase tracking-widest">
            SFXAura · SFX
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        <div>
          <Textarea
            data-testid="prompt-textarea"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={batchMode ? t("batchPlaceholder") : t("promptPlaceholder")}
            className="min-h-[110px] bg-[color:var(--surface-2)] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[color:var(--accent)]/40 focus-visible:border-[color:var(--accent)]/50 resize-none text-base"
          />
          <div className="mt-2 flex items-center justify-between gap-3">
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <Switch
                data-testid="auto-enhance-switch"
                checked={autoEnhance}
                onCheckedChange={toggleAutoEnhance}
                className="data-[state=checked]:bg-[color:var(--accent)]"
              />
              <span className="text-xs text-white/60">{t("autoEnhance")}</span>
            </label>
            <button
              data-testid="enhance-prompt-btn"
              onClick={enhance}
              disabled={enhancing || prompt.trim().length < 2}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[color:var(--accent)]/40 text-[color:var(--accent)] hover:bg-[color:var(--accent)]/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {enhancing ? (
                <>
                  <ArrowsClockwise size={14} className="animate-spin" /> {t("enhancing")}
                </>
              ) : (
                <>
                  <MagicWand size={14} weight="fill" /> {t("enhanceBtn")}
                </>
              )}
            </button>
          </div>
          <div className="mt-3">
            <div className="flex flex-wrap gap-1.5 mb-2">
              {Object.entries(PRESET_LIBRARY).map(([id, g]) => (
                <button
                  key={id}
                  data-testid={`preset-group-${id}`}
                  onClick={() => setPresetGroup(id)}
                  className={`text-[11px] px-2.5 py-1 rounded-md uppercase tracking-wider font-mono-num transition-colors ${
                    presetGroup === id
                      ? "bg-[color:var(--accent)]/15 text-[color:var(--accent)] border border-[color:var(--accent)]/40"
                      : "text-white/40 hover:text-white/70 border border-transparent"
                  }`}
                >
                  {g.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {PRESET_LIBRARY[presetGroup].presets.map((p) => (
                <button
                  key={p.label}
                  data-testid="preset-chip"
                  title={p.prompt}
                  onClick={() => setPrompt(p.prompt)}
                  className="text-xs px-2.5 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 transition-colors"
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-mono-num uppercase tracking-widest text-white/50">{t("duration")}</label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-white/40">{t("auto")}</span>
                <Switch
                  data-testid="auto-duration-switch"
                  checked={autoDuration}
                  onCheckedChange={setAutoDuration}
                  className="data-[state=checked]:bg-[color:var(--accent)]"
                />
              </div>
            </div>
            <Slider
              data-testid="duration-slider"
              value={duration}
              onValueChange={setDuration}
              min={1}
              max={22}
              step={0.5}
              disabled={autoDuration}
              className="py-2"
            />
            <p className="text-sm font-mono-num text-white/80">
              {autoDuration ? t("auto") : `${duration[0].toFixed(1)}s`}
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-mono-num uppercase tracking-widest text-white/50">{t("influence")}</label>
            <Slider
              data-testid="influence-slider"
              value={influence}
              onValueChange={setInfluence}
              min={0}
              max={100}
              step={1}
              className="py-2"
            />
            <p className="text-sm font-mono-num text-white/80">{influence[0]}%</p>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-mono-num uppercase tracking-widest text-white/50">{t("category")}</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger
                data-testid="category-select"
                className="bg-[color:var(--surface-2)] border-white/10 text-white"
              >
                <SelectValue placeholder={t("none")} />
              </SelectTrigger>
              <SelectContent className="bg-[color:var(--surface-2)] border-white/10 text-white">
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id} className="focus:bg-white/10">
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-mono-num uppercase tracking-widest text-white/50">{t("loop")}</label>
            <div className="flex items-center justify-between bg-[color:var(--surface-2)] border border-white/10 rounded-md px-3 h-10">
              <span className="text-sm text-white/70 inline-flex items-center gap-2">
                <InfinityIcon size={16} /> {t("loopable")}
              </span>
              <Switch
                data-testid="loop-switch"
                checked={loop}
                onCheckedChange={setLoop}
                className="data-[state=checked]:bg-[color:var(--accent)]"
              />
            </div>
          </div>
        </div>

        <button
          data-testid="generate-sound-btn"
          onClick={generate}
          disabled={generating || credits <= 0}
          className="btn-primary w-full py-3.5 rounded-xl font-medium text-base inline-flex items-center justify-center gap-2"
        >
          {generating ? (
            <>
              <ArrowsClockwise size={18} className="animate-spin" /> {t("generating")}
            </>
          ) : (
            <>
              <Sparkle size={18} weight="fill" /> {t("generate")}{batchMode && batchProgress ? ` ${batchProgress}` : batchMode ? " ×" + Math.min(5, prompt.split("\n").filter((l) => l.trim().length >= 3).length || 1) : ""}
            </>
          )}
        </button>
      </div>
    </section>
  );
}
